import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Route, Routes } from 'react-router-dom';
import type { OutlineItem } from '../features/viewer/PdfDocument';
import { AppToolbar } from '../features/toolbar/AppToolbar';
import { PdfLibrary } from '../features/library/PdfLibrary';
import { OutlinePanel } from '../features/viewer/OutlinePanel';
import { AuthGate } from '../features/auth/AuthGate';
import { useReaderControls } from '../features/viewer/useReaderControls';
import { useAppStore } from '../store/useAppStore';
import { pdfService } from '../services/pdfService';
import { useTheme } from '../hooks/useTheme';
import { useGlobalStateSync } from '../hooks/useGlobalStateSync';
import { apiUrl } from '../services/api';
import { useUiLanguage } from '../hooks/useUiLanguage';

const PdfViewer = lazy(() =>
  import('../features/viewer/PdfViewer').then((module) => ({ default: module.PdfViewer })),
);
const SettingsPanel = lazy(() =>
  import('../features/settings/SettingsPanel').then((module) => ({
    default: module.SettingsPanel,
  })),
);
const LibraryManager = lazy(() =>
  import('../features/libraries/LibraryManager').then((module) => ({
    default: module.LibraryManager,
  })),
);
const PdfManager = lazy(() => import('../features/pdf-manager/PdfManager'));
const GuidePage = lazy(() => import('../features/guide/GuidePage'));
const NotFound = lazy(() => import('./NotFound'));

function ReaderApp() {
  const [split, setSplit] = useState(70);
  const [outline, setOutline] = useState<OutlineItem[]>([]);
  const fullscreen = useAppStore((state) => state.fullscreen);
  const setItems = useAppStore((state) => state.setItems);
  useReaderControls();
  const load = useCallback(() => {
    void pdfService
      .list()
      .then(setItems)
      .catch(() => setItems([]));
  }, [setItems]);
  const refresh = useCallback(() => {
    void pdfService.syncAll().catch(() => undefined).finally(load);
  }, [load]);
  useEffect(() => {
    load();
    const events = new EventSource(apiUrl('/events'));
    events.addEventListener('pdfs', load);
    return () => events.close();
  }, [load]);
  useEffect(() => {
    if (fullscreen && !document.fullscreenElement)
      void document.documentElement.requestFullscreen?.().catch(() => undefined);
    if (!fullscreen && document.fullscreenElement) void document.exitFullscreen?.();
  }, [fullscreen]);
  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement) {
        const editingPage = window.matchMedia('(max-width: 800px)').matches && document.activeElement?.matches('.page-jump-dialog input');
        if (!editingPage) useAppStore.getState().setFullscreen(false);
      }
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const resize = (event: ReactPointerEvent) => {
    const start = event.clientX,
      initial = split;
    const move = (value: PointerEvent) =>
      setSplit(Math.max(45, Math.min(85, initial + ((value.clientX - start) / innerWidth) * 100)));
    const up = () => {
      removeEventListener('pointermove', move);
      removeEventListener('pointerup', up);
    };
    addEventListener('pointermove', move);
    addEventListener('pointerup', up);
  };
  return (
    <main className={`app-shell ${fullscreen ? 'is-fullscreen' : ''}`}>
      <AppToolbar onRefresh={refresh} />
      <div className="workspace">
        <div id="pdf-viewer-pane" className="viewer-pane" style={{ width: `${split}%` }}>
          <Suspense
            fallback={
              <div className="viewer document-loading">
                <span className="document-spinner" />
              </div>
            }
          >
            <PdfViewer onOutline={setOutline} />
          </Suspense>
        </div>
        <button className="split-handle" aria-label="Resize panels" onPointerDown={resize} />
        <div className="library-pane" style={{ width: `${100 - split}%` }}>
          <PdfLibrary />
        </div>
      </div>
      <SettingsPanel />
      <OutlinePanel items={outline} />
    </main>
  );
}
function AuthenticatedApp() {
  useTheme();
  useGlobalStateSync();
  return (
    <Suspense fallback={<div className="loading">Opening DS-PDF…</div>}>
      <Routes>
        <Route path="/" element={<ReaderApp />} />
        <Route path="/libraries" element={<LibraryManager />} />
        <Route path="/manage" element={<PdfManager />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
export default function App() {
  useUiLanguage();
  return (
    <AuthGate>
      <AuthenticatedApp />
    </AuthGate>
  );
}
